import { environment } from '@environments/environment';

/**
 * Supported application themes.
 */
export const APPLICATION_THEMES = {
  CAIXA: 'caixa',
  CAIXA_GOV: 'caixa-gov',
} as const;

/**
 * Attribute used by the host application to define the theme.
 */
export const APPLICATION_THEME_ATTRIBUTE = 'theme';

/**
 * Query param used to define the application theme.
 */
export const APPLICATION_THEME_QUERY_PARAM = 'theme';

/**
 * Data attribute used internally to scope theme styles.
 */
export const APPLICATION_THEME_DATA_ATTRIBUTE = 'data-caixa-theme';

/**
 * Default application theme.
 */
export const DEFAULT_APPLICATION_THEME = APPLICATION_THEMES.CAIXA;

/**
 * Available application theme values.
 */
export type ApplicationTheme = typeof APPLICATION_THEMES[keyof typeof APPLICATION_THEMES];

/**
 * Applies the current application theme to the provided element.
 */
export function applyApplicationThemeOnElement(
  element: HTMLElement,
  theme: ApplicationTheme,
): void {
  element.setAttribute(APPLICATION_THEME_DATA_ATTRIBUTE, theme);
  element.classList.toggle('caixa-theme-caixa', theme === APPLICATION_THEMES.CAIXA);
  element.classList.toggle('caixa-theme-caixa-gov', theme === APPLICATION_THEMES.CAIXA_GOV);
}

/**
 * Notifies theme-aware consumers that the current palette changed.
 */
export function dispatchApplicationTheme(theme: ApplicationTheme): void {
  window.dispatchEvent(new CustomEvent('paletteChanges', { detail: theme }));
}

/**
 * Applies the current application theme to the document and optionally to a host element.
 */
export function applyApplicationTheme(theme: ApplicationTheme, hostElement?: HTMLElement): void {
  if (hostElement) {
    applyApplicationThemeOnElement(hostElement, theme);
  }

  if (document.body) {
    applyApplicationThemeOnElement(document.body, theme);
  }

  dispatchApplicationTheme(theme);
}

/**
 * Tries to resolve a supported application theme from any external input.
 */
export function resolveApplicationTheme(theme: unknown): ApplicationTheme | null {
  if (typeof theme !== 'string') {
    return null;
  }

  switch (theme.trim().toLowerCase()) {
    case 'gov':
    case 'caixa-gov':
      return APPLICATION_THEMES.CAIXA_GOV;
    case 'caixa':
      return APPLICATION_THEMES.CAIXA;
    default:
      return null;
  }
}

/**
 * Normalizes any incoming theme value to a supported application theme.
 */
export function normalizeApplicationTheme(theme: string | null | undefined): ApplicationTheme {
  return resolveApplicationTheme(theme) ?? DEFAULT_APPLICATION_THEME;
}

/**
 * Resolves a theme from the current query params.
 */
export function resolveApplicationThemeFromQueryParams(search: string): ApplicationTheme | null {
  const queryString = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(queryString);

  return resolveApplicationTheme(params.get(APPLICATION_THEME_QUERY_PARAM));
}

/**
 * Normalizes an origin string to a comparable origin value.
 */
export function normalizeApplicationThemeOrigin(origin: string | null | undefined): string | null {
  if (!origin) {
    return null;
  }

  if (origin === 'null') {
    return 'null';
  }

  try {
    return new URL(origin).origin;
  } catch {
    return null;
  }
}

/**
 * Indicates whether a message origin is allowed to update the application theme.
 */
export function isApplicationThemeMessageOriginAllowed(origin: string): boolean {
  const normalizedOrigin = normalizeApplicationThemeOrigin(origin);
  const allowedOrigin = normalizeApplicationThemeOrigin(environment.allowedThemeMessageOrigin);

  if (!normalizedOrigin || !allowedOrigin) {
    return false;
  }

  return normalizedOrigin === allowedOrigin;
}

/**
 * Resolves a theme from postMessage payloads.
 */
export function resolveApplicationThemeFromMessage(data: unknown): ApplicationTheme | null {
  if (typeof data === 'string') {
    const trimmedData = data.trim();

    if (trimmedData.startsWith('{')) {
      try {
        return resolveApplicationThemeFromMessage(JSON.parse(trimmedData));
      } catch {
        return null;
      }
    }

    const themeFromQueryParams = resolveApplicationThemeFromQueryParams(trimmedData);

    return themeFromQueryParams ?? resolveApplicationTheme(trimmedData);
  }

  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const nestedPayload = payload['payload'];

  return (
    resolveApplicationTheme(payload[APPLICATION_THEME_QUERY_PARAM]) ??
    (nestedPayload && typeof nestedPayload === 'object'
      ? resolveApplicationTheme(
          (nestedPayload as Record<string, unknown>)[APPLICATION_THEME_QUERY_PARAM],
        )
      : null)
  );
}
